<template>
  <div :class="classNames">
    <div
      v-if="loading"
      class="schedule-loading-bar"
      data-cy="schedule-loading-bar"
      role="progressbar"
      aria-label="Loading schedule"
    >
      <span></span>
    </div>
    <div
      :class="{
        'mobile-calendar': breakpoints().sm
      }"
    >
      <full-calendar ref="fullCalendar" :options="frozenOptions">
        <template #eventContent="context">
          <timeline-event :context="context" v-bind="$attrs"></timeline-event>
        </template>
        <template #resourceLabelContent="{ resource }">
          <timeline-resource
            :resource="resource"
            v-bind="$attrs"
          ></timeline-resource>
        </template>
      </full-calendar>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import moment from "moment-timezone";
import { markRaw } from "vue";
import { mapState } from "vuex";
import cable from "@/lib/cable.js";
import { trackRumAction } from "@/lib/datadog.js";
import { EventBus } from "@/lib/event_bus";

import AdminTimeClockChannel from "@/channels/AdminTimeClockChannel.js";
import CONSTANTS from "@/Constants.js";
import { breakpoints } from "@/mixins";
import interactionPlugin from "@fullcalendar/interaction";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import FullCalendar from "@fullcalendar/vue3";
import TimelineEvent from "./TimelineEvent.vue";
import TimelineResource from "./TimelineResource.vue";
const { FULLCALENDAR_LICENSE_KEY, SCHEDULE_DEFAULT_ZOOM } = CONSTANTS;

const DEFAULT_SCROLL_TIME = "06:00:00";
const SCHEDULE_MIN_HEIGHT = 320;

export default {
  components: {
    FullCalendar,
    TimelineEvent,
    TimelineResource
  },
  props: {
    options: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Object,
      default: null
    },
    preWorkEnabled: {
      type: Boolean,
      default: false
    },
    zoom: {
      type: Number,
      default: SCHEDULE_DEFAULT_ZOOM
    },
    classNames: {
      type: [String, Array, Object],
      default: ""
    }
  },
  data() {
    return {
      loading: false,
      resizeTimeout: null,
      channel: null,
      // Stable, non-reactive snapshot of the options handed to <full-calendar>.
      // The vendored @fullcalendar/vue3 wrapper deep-watches its `options` prop
      // and calls getApi().pauseRendering() in the handler; getApi() is
      // undefined until the wrapper mounts, so any change to the options
      // identity during the mount flush throws. Built once in created() and
      // markRaw'd so Vue never deep-tracks it, the watcher never fires. The one
      // dynamic option (slotMinWidth, from zoom) is applied imperatively.
      frozenOptions: null
    };
  },
  computed: {
    ...mapState({
      timeCardsEnabled({ tenant }) {
        return tenant?.time_cards_enabled;
      },
      isMobileScreenSize() {
        return breakpoints().sm;
      }
    }),
    weekViewOptions() {
      const shared = {
        eventTimeFormat: {
          hour: "numeric",
          minute: "2-digit",
          meridiem: "short"
        },
        displayEventEnd: false,
        // Duration edits stay in day view; week-view bars are not resizable.
        eventDurationEditable: false,
        navLinks: true,
        navLinkDayClick: this.handleNavLinkDayClick
      };

      if (this.isMobileScreenSize) {
        // Mobile keeps the day-cell layout: day-wide slats put the axis in
        // date-scale so each appointment is a full-width bar that stacks
        // chronologically. A business-hours timeline would be an unreadable
        // sliver per day at phone widths.
        return {
          ...shared,
          slotDuration: { days: 1 },
          snapDuration: { days: 1 },
          slotLabelFormat: [
            { weekday: "short", month: "numeric", day: "numeric" }
          ],
          slotMinWidth: 150,
          scrollTime: "00:00:00"
        };
      }

      // Desktop is a proportional business-hours timeline: each day is an
      // hour-scaled mini-axis (7a–7p) so events sit at their real start time
      // and open gaps within a day are visible. Two-tier labels show the day
      // on top and hour ticks below. The 12-hour window must stay an exact
      // multiple of slotLabelInterval (12 / 4 = 3) — otherwise FullCalendar
      // counts the interval continuously across the 7-day axis and the tick
      // clock-times drift per day (7a/11a/3p becomes 8a/12p/4p the next day).
      // snapDuration keeps cross-day drag landing on 15-minute increments.
      return {
        ...shared,
        slotMinTime: "07:00:00",
        slotMaxTime: "19:00:00",
        slotDuration: "01:00:00",
        snapDuration: "00:15:00",
        slotLabelInterval: { hours: 4 },
        slotLabelFormat: [
          { weekday: "short", month: "numeric", day: "numeric" },
          { hour: "numeric", meridiem: "short" }
        ],
        // Per hour-slot; day width = 12 × this. Wide enough for event chips
        // to be legible without zoom (the week board scrolls horizontally).
        slotMinWidth: 38,
        eventMinWidth: 36,
        scrollTime: "07:00:00"
      };
    },
    calendarOptions() {
      return {
        plugins: [
          interactionPlugin,
          resourceTimelinePlugin,
          momentTimezonePlugin
        ],
        themeSystem: "bootstrap4",
        initialView: "resourceTimelineDay",
        // Weeks start Monday. Must be top-level: FullCalendar ignores
        // firstDay inside a views entry. No effect on the single-day view.
        firstDay: 1,
        views: {
          resourceTimelineWeek: this.weekViewOptions
        },
        schedulerLicenseKey: FULLCALENDAR_LICENSE_KEY,
        editable: true,
        eventStartEditable: true,
        eventResourceEditable: true,
        eventDurationEditable: true,
        eventResizableFromStart: false,
        eventAllow: (_dropInfo, draggedEvent) => {
          // Allow interaction only for appointments
          const isCalendarEvent = Boolean(
            draggedEvent.extendedProps?.calendar_event
          );
          return !isCalendarEvent;
        },
        eventResize: this.handleEventResize,
        headerToolbar: {
          left: "",
          center: "",
          right: ""
        },
        nowIndicator: true,
        scrollTime: DEFAULT_SCROLL_TIME,
        slotMinWidth: this.zoom,
        refetchResourcesOnNavigate: true,
        eventOrder: "-schedule,-travel,start",
        eventMinWidth: 0,
        eventClick: (info) => {
          const props = info.event.extendedProps;
          if (props.calendar_event) {
            EventBus.$emit("edit-calendar-event", props.event);
          } else if (!props.travel && !props.pre_work && !props.schedule) {
            tippy.hideAll();
            const realId = props._appointmentId || info.event.id;
            this.$emit("open-edit-modal", realId);
          }
        },
        dateClick: (info) => {
          if (info.view.type === "resourceTimelineDay") {
            // Only handle double-click for empty slots
            if (info.jsEvent.detail === 2 && info.resource) {
              this.handleEmptySlotDoubleClick(info);
            }
          }
        },
        eventClassNames: this.setEventClassName,
        // Fires on every resource/event fetch (nav, view switch, filter,
        // 60s poll). Drives the top progress bar; ScheduleBoard mirrors it
        // into the header "Updating…" label.
        loading: (isLoading) => {
          this.loading = isLoading;
          this.$emit("loading-change", isLoading);
        },
        datesSet: (dateInfo) => {
          // datesSet fires right after the first render (and on every nav), when
          // getApi() is reliably available — so guarantee the global handle is
          // registered even if it was null at mount (ENG-242).
          this.registerCalendarHandle();
          // Update calendar height after calls are rendered
          this.api()?.setOption("height", this.getCalendarHeight());
          // Chain datesSet call
          if (this.options.datesSet) {
            this.options.datesSet(dateInfo);
          }
        },
        resourceAreaWidth: this.isMobileScreenSize ? 100 : 150,
        resourceAreaColumns: [
          {
            headerContent: "Technicians",
            field: "title"
          }
        ],
        resourceGroupField: "department_name",
        resourceOrder: "department_name, installer, title",
        resourceGroupLabelContent: function (e) {
          // display Unassigned as dept name without sorting unassigned to bottom
          return e.groupValue || "Unassigned";
        },
        // FIXME: Auto-collapse based on cache.
        // resourceGroupLabelDidMount: function(info) {
        //   try {
        //     let cachedRows = localStorage.getItem(
        //       "scheduleBoardCollapsedRows"
        //     );
        //     let collapsedRows = cachedRows ? JSON.parse(cachedRows) : [];
        //     if (collapsedRows.includes(info.groupValue)) {
        //       info.el.querySelector(".fc-datagrid-expander").click();
        //     }
        //   } catch (e) {
        //     console.error(e);
        //   }
        // },
        resourceLaneWillUnmount: function (info) {
          if (info.resource.id == 0) {
            // Unassigned was closed
            // add class to disable second sticky row
            $(".fc-resource-timeline").addClass("unassigned-collapsed");
          }
        },
        resourceLaneDidMount: function (info) {
          if (info.resource.id == 0) {
            // Unassigned was opened
            // add class to disable second sticky row
            $(".fc-resource-timeline").removeClass("unassigned-collapsed");
          }
        },
        ...this.options
      };
    }
  },
  watch: {
    timeCardsEnabled(newValue, oldValue) {
      if (newValue !== oldValue && newValue) {
        this.connectToChannel();
      }
    },
    // slotMinWidth is the only reactive calendar option; apply it imperatively
    // so the frozen options object never has to be replaced (which would fire
    // the wrapper's deep `options` watcher).
    zoom(value) {
      this.api()?.setOption("slotMinWidth", value);
    }
  },
  created() {
    this.frozenOptions = markRaw(this.calendarOptions);
  },
  mounted() {
    this.attachDOMListeners();
    // Register the global calendar handle the filter bar / SchedulePage use to
    // navigate via window._FC.gotoDate. Also registered from datesSet, because
    // getApi() can be null during the mount flush — that race left window._FC
    // null and silently broke day navigation until a full refresh (ENG-242).
    this.registerCalendarHandle();
    if (this.timeCardsEnabled) {
      this.connectToChannel();
    }
  },
  beforeUnmount() {
    this.removeDOMListeners();
    if (this.channel) this.channel.disconnect();
    // Never leave the global handle pointing at a torn-down calendar.
    if (window._FC === this.api()) {
      window._FC = null;
    }
  },
  methods: {
    // The live FullCalendar API handle. MUST stay a method, not a computed:
    // its getter reads only non-reactive state (a template ref + getApi()), so
    // as a computed it tracks zero reactive deps and — since Vue 3.5.14
    // (vuejs/core#12341) — would cache its first value forever. If that first
    // read lands during the mount flush before <full-calendar> has built its
    // Calendar, the cached value is null permanently and day navigation
    // silently no-ops until a full page refresh. A method re-reads live.
    api() {
      return this.$refs.fullCalendar?.getApi() ?? null;
    },
    // Publish this calendar's API as the global nav handle (window._FC), used by
    // ScheduleBoardFilters / SchedulePage to drive gotoDate. getApi() can return
    // null during the mount flush, which left window._FC null and silently broke
    // day navigation until a full page refresh (ENG-242); calling this from both
    // mounted() and datesSet guarantees the handle registers once the calendar is
    // actually ready. No-op once this instance is already the registered handle.
    registerCalendarHandle() {
      const api = this.api();
      if (!api || window._FC === api) return;
      // Observability (ENG-241): a different timeline replacing an existing
      // handle (route re-entry, v1<->v2 swap) can mean nav was driving a
      // detached calendar.
      if (window._FC && window._FC !== api) {
        trackRumAction("schedule_fc_handle_replaced", { had_previous: true });
      }
      window._FC = api;
    },
    async connectToChannel() {
      this.channel = new AdminTimeClockChannel();

      if (this.channel) {
        try {
          await cable.subscribe(this.channel).ensureSubscribed();
          this.channel?.on(
            "tenant-clock-in-status-change",
            this.handleStatusChange
          );
        } catch (err) {
          console.log("App Unable to join Cable server", err);
        }
      }
    },
    handleStatusChange(payload) {
      const { user_id, action } = payload;
      const clockedInIcon = "clocked-in-icon";
      const clockedOutIcon = "clocked-out-icon";
      if (action === "clock_in") {
        $(`.${user_id}-${clockedInIcon}`).removeClass("hidden");
        $(`.${user_id}-${clockedOutIcon}`).addClass("hidden");
      } else {
        $(`.${user_id}-${clockedInIcon}`).addClass("hidden");
        $(`.${user_id}-${clockedOutIcon}`).removeClass("hidden");
      }
    },
    attachDOMListeners() {
      // Adjust schedule height on page resize
      window.addEventListener("resize", this.handleScreenResize);
      window.addEventListener("scroll", this.handleScreenResize);
      // Apply hover class to resource and event rows
      $(document).on(
        "mouseenter",
        "[data-resource-id]",
        this.handleRowMouseEnter
      );
      // Remove hover class to resource and event rows
      $(document).on(
        "mouseleave",
        "[data-resource-id]",
        this.handleRowMouseLeave
      );
      // Collapse resource group on label click
      $(document).on(
        "click",
        ".fc-datagrid-cell.fc-resource-group .fc-datagrid-cell-main",
        this.collapseRow
      );
      // Cache collapsed rows
      // $(document).on("click", ".fc-datagrid-expander", this.cacheCollapsedRow);
    },
    removeDOMListeners() {
      window.removeEventListener("resize", this.handleScreenResize);
      window.removeEventListener("scroll", this.handleScreenResize);
      $(document).off(
        "mouseenter",
        "[data-resource-id]",
        this.handleRowMouseEnter
      );
      $(document).off(
        "mouseleave",
        "[data-resource-id]",
        this.handleRowMouseLeave
      );
      $(document).off(
        "click",
        ".fc-datagrid-cell.fc-resource-group .fc-datagrid-cell-main",
        this.collapseRow
      );
      // $(document).off("click", ".fc-datagrid-expander", this.cacheCollapsedRow);
    },
    collapseRow(e) {
      $(e.target).siblings(".fc-datagrid-expander")[0].click();
    },
    cacheCollapsedRow(e) {
      try {
        const resourceName = $(e.target)
          .siblings(".fc-datagrid-cell-main")
          .text();
        const isClosed = $(e.target).children(".fc-icon-plus-square")[0];
        const cachedRows = localStorage.getItem("scheduleBoardCollapsedRows");
        let collapsedRows = cachedRows ? JSON.parse(cachedRows) : [];
        const isCollapsedCached = collapsedRows.includes(resourceName);
        console.log(isCollapsedCached);
        // FIXME: Fullcalendar bug on initial render making the isClosed check fail
        // need a better way to determine if a row was opened or collapsed
        if (isClosed) {
          collapsedRows.push(resourceName);
        } else if (!isCollapsedCached) {
          collapsedRows = collapsedRows.filter((name) => name !== resourceName);
        }
        localStorage.setItem(
          "scheduleBoardCollapsedRows",
          JSON.stringify(collapsedRows)
        );
      } catch (e) {
        console.error(e);
      }
    },
    handleRowMouseEnter(e) {
      const resource_id = $(e.currentTarget).data("resource-id");
      if (resource_id != 0 && resource_id) {
        return $("[data-resource-id=" + resource_id + "]").addClass(
          "hovered-row"
        );
      }
    },
    handleRowMouseLeave() {
      $(".hovered-row").removeClass("hovered-row");
    },
    handleScreenResize: debounce(function () {
      // wait for header wrap css changes on small screen sizes
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.api()?.setOption("height", this.getCalendarHeight());
      }, 50);
    }, 150),
    getCalendarHeight() {
      const schedule = $(".orders-calendar")[0];
      if (!schedule) return;
      if (this.isMobileScreenSize) return "auto";
      // Get main page padding minus scroll position
      const PAGE_TOP_PADDING = Math.max(90 - window.scrollY, 30);
      // Get visible height of page header
      const headerHeight = schedule.getBoundingClientRect().top;
      // Subtract top headers from viewport height
      const calculatedScreenSpace =
        window.innerHeight - (headerHeight + PAGE_TOP_PADDING);
      return Math.max(calculatedScreenSpace, SCHEDULE_MIN_HEIGHT);
    },
    handleNavLinkDayClick(date) {
      // Week-view day headers bridge back to the day board for hour-level work
      this.$emit("switch-to-day", date);
    },
    handleEmptySlotDoubleClick(info) {
      // Extract user ID from resource (needed for technician selection)
      // Skip if this is the "Unassigned" row (id: 0)
      const technicianUserId =
        info.resource.id === 0
          ? null
          : parseInt(info.resource.extendedProps.user_id) || null;

      // Round down to the start of the hour so clicking anywhere within an hour
      // selects the time slot starting at that hour
      const clickedMoment = moment(info.date).startOf("hour");

      // Check if the scheduled_time for this slot would be in the past
      // (scheduled_time = arrival_window_start + 1 hour, and isPast checks scheduled_time)
      const now = moment();
      const potentialScheduledTime = clickedMoment.clone().add(1, "hour");
      if (potentialScheduledTime.isSameOrBefore(now)) {
        // Cannot schedule an appointment in the past - show validation error
        this.$emit(
          "validation-error",
          "Cannot schedule an appointment in the past."
        );
        return;
      }

      // Add 1 hour to match the backend pattern where scheduled_time = arrival_window_start + 1 hour
      const scheduledTime = clickedMoment
        .clone()
        .add(1, "hour")
        .format("YYYY-MM-DDTHH:mm:ssZ");

      // Emit an event to open the appointment create modal
      this.$emit("open-create-appointment-modal", {
        scheduled_time: scheduledTime,
        technician_id: technicianUserId
      });
    },
    setEventClassName(context) {
      const { event } = context;
      const {
        travel,
        schedule,
        late,
        has_travel,
        needs_attention,
        ghost,
        status,
        assigned,
        pre_work,
        calendar_event
      } = event.extendedProps;
      const classNames = ["elevation-1"];
      if (travel) {
        const isFlat = moment(event.end).diff(event.start, "minutes") < 2;
        if (isFlat) {
          classNames.push("flat-travel");
        }
        classNames.push("travel");
      }
      if (pre_work) {
        classNames.push("prework");
      }
      if (schedule || calendar_event) {
        classNames.push("schedule-event");

        if (calendar_event) {
          if (event.extendedProps.event.exclusive) {
            classNames.push("exclusive");
          } else {
            classNames.push("available");
          }
        }
      }
      if (late) {
        classNames.push("late-event");
      }
      if (has_travel) {
        classNames.push("has-travel");
      }
      if (needs_attention) {
        classNames.push("needs-attention");
      }
      // Adds ghost class
      if (ghost) {
        classNames.push("ghost");
      } else {
        classNames.push("normal");
      }
      if (status) {
        // Adds appointment status classname
        classNames.push(status);
      }
      const realId = event.extendedProps._appointmentId || event.id;
      if (realId) {
        // Adds appointment id to event element
        // Used for loading edit modal and context menu
        classNames.push(`event-id-${realId}`);
      }
      if (assigned && status === "pending") {
        classNames.push("assigned");
      }
      return classNames;
    },
    handleEventResize(info) {
      // Only Appointments are resizable
      const isCalendarEvent = Boolean(info.event.extendedProps?.calendar_event);
      if (isCalendarEvent) {
        info.revert();
        return;
      }

      const durationMinutes = moment
        .duration(moment(info.event.end).diff(moment(info.event.start)))
        .asMinutes();

      const appointmentId =
        info.event.extendedProps._appointmentId || info.event.id;
      this.$emit("resize-appointment", {
        appointmentId,
        duration_minutes: durationMinutes
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.loader-img {
  height: 180px;
  margin: 20px 0;
}
.loader-body {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

// Indeterminate top progress bar shown while the board is fetching.
// Pinned to the top edge of .orders-calendar (made relative in calendar.scss).
.schedule-loading-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  overflow: hidden;
  background: rgba(37, 99, 235, 0.15);
  z-index: 50;

  span {
    position: absolute;
    top: 0;
    height: 100%;
    width: 45%;
    background: linear-gradient(
      90deg,
      transparent,
      #2563eb 40%,
      #1d4ed8 60%,
      transparent
    );
    box-shadow: 0 0 6px rgba(37, 99, 235, 0.5);
    animation: schedule-loading-slide 1.1s ease-in-out infinite;
  }
}

@keyframes schedule-loading-slide {
  0% {
    left: -45%;
  }
  100% {
    left: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .schedule-loading-bar span {
    animation: schedule-loading-pulse 1.2s ease-in-out infinite;
    left: 0;
    width: 100%;
  }
}

@keyframes schedule-loading-pulse {
  0%,
  100% {
    opacity: 0.35;
  }
  50% {
    opacity: 1;
  }
}
</style>

<style>
.mobile-calendar {
  min-height: 320px;
  .fc .fc-scrollgrid-section-header.fc-scrollgrid-section-sticky > * {
    top: -25px;
  }
  .fc-resource-timeline-divider {
    top: 0 !important;
  }
}
</style>
